import React from "/vendor/react";
import PropTypes from "prop-types";
import gql from "/vendor/graphql-tag";

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "/vendor/@material-ui/core";
import { ListController } from "/lib/component/controller";
import { Loader, TableListEmptyState } from "/lib/component/base";
import { useFilterParams } from "/lib/component/util";
import { useManageResourceStatusToast } from "/lib/component/toast";
import HandlerConfigDialog from "/lib/component/partial/HandlerConfigDialog";
import Pagination from "/lib/component/partial/Pagination";
import HandlersListHeader from "./HandlersListHeader";
import HandlersListItem from "./HandlersListItem";

const HandlersList = ({
  editable,
  loading,
  limit,
  namespace,
  namespaceName,
  offset,
  order,
  onChangeQuery,
  onChangeFilters,
  onDeleteHandler,
  refetch,
}) => {
  const [filters] = useFilterParams();
  const createManageResourceStatusToast = useManageResourceStatusToast();

  // null = closed, { handlerName: null } = new handler, { handlerName: "x" } = edit
  const [configDialog, setConfigDialog] = React.useState(null);

  const items = namespace
    ? namespace.handlers.nodes.filter(hd => !hd.deleted)
    : [];

  const resolvedNamespace =
    namespaceName || (namespace && namespace.name) || "";

  const deleteHandlers = handlers => {
    createManageResourceStatusToast(
      Promise.all(handlers.map(handler => onDeleteHandler({ id: handler.id }))),
      {
        resourceType: "handler",
        action: "delete",
        resourceName: handlers.length === 1 ? handlers[0].name : undefined,
        batch: handlers.length > 1,
      },
    );
  };

  return (
    <ListController
      items={items}
      getItemKey={handler => handler.id}
      renderEmptyState={() => {
        return (
          <TableRow>
            <TableCell>
              <TableListEmptyState
                loading={loading}
                primary="No results matched your query."
                secondary="
              Try refining your search query in the search box. The filter
              buttons above are also a helpful way of quickly finding entities.
            "
              />
            </TableCell>
          </TableRow>
        );
      }}
      renderItem={({
        key,
        item: handler,
        hovered,
        setHovered,
        selectedCount,
        selected,
        setSelected,
      }) => (
        <HandlersListItem
          key={key}
          editable={editable}
          editing={selectedCount > 0}
          handler={handler}
          hovered={hovered}
          onHover={editable ? setHovered : () => null}
          selected={selected}
          onChangeSelected={setSelected}
          onClickEdit={() => setConfigDialog({ handlerName: handler.name })}
          onClickDelete={() => deleteHandlers([handler])}
        />
      )}
    >
      {({ children, selectedItems, setSelectedItems, toggleSelectedItems }) => (
        <Paper>
          <Loader loading={loading}>
            <HandlersListHeader
              editable={editable}
              filters={filters}
              selectedItems={selectedItems}
              rowCount={children.length || 0}
              order={order}
              onChangeFilters={onChangeFilters}
              onChangeQuery={onChangeQuery}
              onClickSelect={toggleSelectedItems}
              onClickNew={() => setConfigDialog({ handlerName: null })}
              onClickDelete={() => {
                deleteHandlers(selectedItems);
                setSelectedItems([]);
              }}
            />
            <Table>
              <TableBody>{children}</TableBody>
            </Table>

            <Pagination
              limit={limit}
              offset={offset}
              pageInfo={namespace && namespace.handlers.pageInfo}
              onChangeQuery={ch => onChangeQuery(ex => ({ ...ex, ...ch }))}
            />

            {configDialog && (
              <HandlerConfigDialog
                namespace={resolvedNamespace}
                handlerName={configDialog.handlerName}
                onClose={() => setConfigDialog(null)}
                onSuccess={() => {
                  setSelectedItems([]);
                  refetch();
                }}
              />
            )}
          </Loader>
        </Paper>
      )}
    </ListController>
  );
};

HandlersList.propTypes = {
  editable: PropTypes.bool,
  namespace: PropTypes.shape({
    handlers: PropTypes.shape({
      nodes: PropTypes.array.isRequired,
    }),
  }),
  namespaceName: PropTypes.string,
  loading: PropTypes.bool,
  onChangeFilters: PropTypes.func,
  onChangeQuery: PropTypes.func.isRequired,
  onDeleteHandler: PropTypes.func,
  refetch: PropTypes.func,
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  order: PropTypes.string.isRequired,
};

HandlersList.defaultProps = {
  editable: true,
  namespace: null,
  namespaceName: undefined,
  loading: false,
  limit: undefined,
  offset: undefined,
  onChangeFilters: () => null,
  onDeleteHandler: () => Promise.resolve(),
  refetch: () => null,
};

HandlersList.fragments = {
  namespace: gql`
    fragment HandlersList_namespace on Namespace {
      name

      handlers(
        limit: $limit
        offset: $offset
        orderBy: $order
        filters: $filters
      ) @connection(key: "handlers", filter: ["filters", "orderBy"]) {
        nodes {
          id
          deleted @client
          ...HandlersListItem_handler
        }

        pageInfo {
          ...Pagination_pageInfo
        }
      }
    }
    ${HandlersListItem.fragments.handler}
    ${Pagination.fragments.pageInfo}
  `,
};

export default HandlersList;
